/**
 * Stock Loss Summary — "Where am I losing stock, and how much?"
 * Groups approved counts by location (where) or month (when). Expand any row to
 * see exactly which items caused the loss there. Scoped to the header branch.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FiTrendingDown, FiTrendingUp, FiMapPin, FiClipboard, FiDownload, FiPrinter,
  FiChevronRight, FiChevronDown, FiSearch,
} from 'react-icons/fi';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import { colors } from '../../theme/colors';
import { loadAudits, loadAuditLines } from '../../utils/stockAuditHelper';
import {
  SummaryCards, SummaryCard, SummaryIcon, SummaryInfo, SummaryLabel, SummaryValue,
  FiltersCard, FiltersRow, FilterGroup, Label, Input, Select, ButtonGroup, Button,
  ContentCard, TableContainer, Table, Th, Td, TotalRow, EmptyState,
  Short, Extra, ExpandBtn, ParentRow, SubRow, SubHeadRow, SearchWrap, MiniNote,
  printReport, downloadCsv, csvCell,
} from './auditReportStyles';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (ym) => {
  const [y, m] = String(ym).split('-');
  return `${MONTHS[Number(m) - 1] || ''} ${y}`;
};

const StockLossSummary = () => {
  const { activeCompany, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();
  const companyId = activeCompany?.id;

  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [groupBy, setGroupBy] = useState('location'); // location | month

  // Item-level filters for the expanded detail
  const [itemSearch, setItemSearch] = useState('');
  const [itemType, setItemType] = useState('short'); // short | extra | all

  const [expanded, setExpanded] = useState({});   // groupKey -> true
  const [itemsFor, setItemsFor] = useState({});   // groupKey -> aggregated items[]

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const a = await loadAudits(companyId);
      // Only APPROVED counts represent real, posted stock loss.
      setAudits(a.filter(x => x.status === 'Approved'));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => audits.filter(a => {
    if (activeBranch && activeBranch.id !== 'all' && a.branchId !== activeBranch.id) return false;
    const d = String(a.startedAt || '').slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  }), [audits, activeBranch, from, to]);

  const rows = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      const key = groupBy === 'month'
        ? String(a.startedAt || '').slice(0, 7)
        : a.locationName || '—';
      if (!map[key]) map[key] = { key, branch: a.branchName || '', counts: 0, short: 0, extra: 0, auditIds: [] };
      map[key].counts++;
      map[key].short += a.totals?.shortageValue || 0;
      map[key].extra += a.totals?.excessValue || 0;
      map[key].auditIds.push(a.id);
    });
    return Object.values(map)
      .map(r => ({ ...r, net: r.short - r.extra }))
      .sort((a, b) => (groupBy === 'month' ? a.key.localeCompare(b.key) : b.net - a.net));
  }, [filtered, groupBy]);

  // Reset opened detail when the grouping/filters change (keys become invalid).
  useEffect(() => { setExpanded({}); setItemsFor({}); }, [groupBy, from, to, activeBranch]);

  const totals = useMemo(() => rows.reduce((t, r) => {
    t.counts += r.counts; t.short += r.short; t.extra += r.extra; t.net += r.net;
    return t;
  }, { counts: 0, short: 0, extra: 0, net: 0 }), [rows]);

  const worst = useMemo(() => {
    const byLoc = {};
    filtered.forEach(a => {
      const k = a.locationName || '—';
      byLoc[k] = (byLoc[k] || 0) + ((a.totals?.shortageValue || 0) - (a.totals?.excessValue || 0));
    });
    const top = Object.entries(byLoc).sort((a, b) => b[1] - a[1])[0];
    return top && top[1] > 0 ? top : null;
  }, [filtered]);

  /** Expand a group → pull every count in it and add the items up. */
  const toggle = async (r) => {
    const open = !expanded[r.key];
    setExpanded(p => ({ ...p, [r.key]: open }));
    if (open && !itemsFor[r.key]) {
      try {
        const all = await Promise.all(r.auditIds.map(id => loadAuditLines(companyId, id).catch(() => [])));
        const map = {};
        all.flat().forEach(l => {
          const v = Number(l.variance) || 0;
          if (Math.abs(v) <= 0.0001) return;
          if (!map[l.itemId]) {
            map[l.itemId] = {
              itemId: l.itemId, itemName: l.itemName || '(unknown)', itemCode: l.itemCode || '',
              shortQty: 0, extraQty: 0, shortValue: 0, extraValue: 0,
            };
          }
          const it = map[l.itemId];
          const val = Math.abs(v) * (Number(l.unitCost) || 0);
          if (v < 0) { it.shortQty += Math.abs(v); it.shortValue += val; }
          else { it.extraQty += v; it.extraValue += val; }
        });
        const list = Object.values(map)
          .map(i => ({ ...i, netValue: i.shortValue - i.extraValue }))
          .sort((a, b) => b.netValue - a.netValue);
        setItemsFor(p => ({ ...p, [r.key]: list }));
      } catch (e) { setItemsFor(p => ({ ...p, [r.key]: [] })); }
    }
  };

  const filterItems = useCallback((list) => {
    const q = itemSearch.trim().toLowerCase();
    return (list || []).filter(i => {
      if (itemType === 'short' && i.shortQty <= 0) return false;
      if (itemType === 'extra' && i.extraQty <= 0) return false;
      if (q && !(`${i.itemName} ${i.itemCode}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [itemSearch, itemType]);

  const label = groupBy === 'month' ? 'Month' : 'Location';
  const COLS = 7;

  const handleExport = () => {
    let csv = `${label},Branch,Counts Done,Stock Short,Stock Extra,Net Loss\n`;
    rows.forEach(r => {
      csv += [
        csvCell(groupBy === 'month' ? monthLabel(r.key) : r.key), csvCell(r.branch),
        r.counts, Math.round(r.short * 100) / 100, Math.round(r.extra * 100) / 100, Math.round(r.net * 100) / 100,
      ].join(',') + '\n';
    });
    downloadCsv(`Stock_Loss_Summary_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const handlePrint = () => {
    const body = rows.map(r => `<tr>
      <td>${groupBy === 'month' ? monthLabel(r.key) : r.key}</td>
      <td>${r.branch || '-'}</td>
      <td class="r">${r.counts}</td>
      <td class="r short">${formatCurrency(r.short)}</td>
      <td class="r extra">${formatCurrency(r.extra)}</td>
      <td class="r ${r.net > 0 ? 'short' : 'extra'}">${formatCurrency(r.net)}</td>
    </tr>`).join('');
    printReport('Stock Loss Summary',
      `${activeCompany?.companyName || ''}${activeBranch?.branchName && activeBranch.id !== 'all' ? ' | Branch: ' + activeBranch.branchName : ''} | ${from || 'All'} to ${to || 'All'} | By ${label}`,
      `<table><thead><tr><th>${label}</th><th>Branch</th><th class="r">Counts Done</th>
        <th class="r">Stock Short</th><th class="r">Stock Extra</th><th class="r">Net Loss</th>
       </tr></thead><tbody>${body}</tbody>
       <tfoot><tr><td colspan="3">Total</td>
        <td class="r short">${formatCurrency(totals.short)}</td>
        <td class="r extra">${formatCurrency(totals.extra)}</td>
        <td class="r short">${formatCurrency(totals.net)}</td></tr></tfoot></table>`);
  };

  return (
    <PageLayout pageTitle="Stock Loss Summary" breadcrumb="Reports / Audit Reports / Stock Loss Summary">
      <SummaryCards>
        <SummaryCard>
          <SummaryIcon color={colors.error.main}><FiTrendingDown /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Net Stock Loss</SummaryLabel>
            <SummaryValue style={{ color: totals.net > 0 ? colors.error.main : colors.success.main }}>
              {formatCurrency(totals.net)}
            </SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color="#E65100"><FiMapPin /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Losing Most</SummaryLabel>
            <SummaryValue title={worst ? worst[0] : ''}>{worst ? worst[0] : '—'}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color={colors.success.main}><FiTrendingUp /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Stock Extra Found</SummaryLabel>
            <SummaryValue style={{ color: colors.success.main }}>{formatCurrency(totals.extra)}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color={colors.primary.main}><FiClipboard /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Counts Done</SummaryLabel><SummaryValue>{totals.counts}</SummaryValue></SummaryInfo>
        </SummaryCard>
      </SummaryCards>

      <FiltersCard>
        <FiltersRow>
          <FilterGroup><Label>From Date</Label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} /></FilterGroup>
          <FilterGroup><Label>To Date</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} /></FilterGroup>
          <FilterGroup>
            <Label>Group By</Label>
            <Select value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="location">Location (where)</option>
              <option value="month">Month (when)</option>
            </Select>
          </FilterGroup>
          <FilterGroup style={{ minWidth: 220 }}>
            <Label>Search Item (on expand)</Label>
            <SearchWrap>
              <FiSearch size={14} />
              <Input value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder="Item name or code…" />
            </SearchWrap>
          </FilterGroup>
          <FilterGroup>
            <Label>Show Items</Label>
            <Select value={itemType} onChange={e => setItemType(e.target.value)}>
              <option value="short">Only short items</option>
              <option value="extra">Only extra items</option>
              <option value="all">All items with a difference</option>
            </Select>
          </FilterGroup>
          <ButtonGroup>
            <Button onClick={handleExport} disabled={!rows.length}><FiDownload /> CSV</Button>
            <Button onClick={handlePrint} disabled={!rows.length}><FiPrinter /> Print</Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      <ContentCard>
        <TableContainer>
          {loading ? <EmptyState>Loading…</EmptyState>
            : rows.length === 0 ? <EmptyState>No approved stock counts yet — approve a count to see stock loss here.</EmptyState> : (
              <Table>
                <thead><tr>
                  <Th style={{ width: 30 }} />
                  <Th>{label}</Th><Th>Branch</Th><Th align="right">Counts Done</Th>
                  <Th align="right">Stock Short</Th><Th align="right">Stock Extra</Th><Th align="right">Net Loss</Th>
                </tr></thead>
                <tbody>
                  {rows.map(r => {
                    const isOpen = !!expanded[r.key];
                    const items = filterItems(itemsFor[r.key]);
                    return (
                      <React.Fragment key={r.key}>
                        <ParentRow $expanded={isOpen} onClick={() => toggle(r)}>
                          <Td><ExpandBtn>{isOpen ? <FiChevronDown /> : <FiChevronRight />}</ExpandBtn></Td>
                          <Td><b>{groupBy === 'month' ? monthLabel(r.key) : r.key}</b></Td>
                          <Td>{r.branch || '—'}</Td>
                          <Td align="right">{r.counts}</Td>
                          <Td align="right"><Short>{formatCurrency(r.short)}</Short></Td>
                          <Td align="right"><Extra>{formatCurrency(r.extra)}</Extra></Td>
                          <Td align="right">
                            {r.net > 0 ? <Short>{formatCurrency(r.net)}</Short> : <Extra>{formatCurrency(r.net)}</Extra>}
                          </Td>
                        </ParentRow>

                        {isOpen && !itemsFor[r.key] && (
                          <SubRow><Td colSpan={COLS}><MiniNote>Loading items…</MiniNote></Td></SubRow>
                        )}

                        {isOpen && itemsFor[r.key] && (
                          <>
                            <SubHeadRow>
                              <Td />
                              <Td colSpan={2}>Item</Td>
                              <Td>Code</Td>
                              <Td align="right">Qty Short</Td>
                              <Td align="right">Qty Extra</Td>
                              <Td align="right">Net Loss</Td>
                            </SubHeadRow>
                            {items.length === 0 ? (
                              <SubRow><Td colSpan={COLS}><MiniNote>No items match the item filter.</MiniNote></Td></SubRow>
                            ) : items.map(i => (
                              <SubRow key={i.itemId}>
                                <Td />
                                <Td colSpan={2}>{i.itemName}</Td>
                                <Td>{i.itemCode}</Td>
                                <Td align="right">{i.shortQty ? <Short>{Math.round(i.shortQty * 100) / 100}</Short> : '—'}</Td>
                                <Td align="right">{i.extraQty ? <Extra>{Math.round(i.extraQty * 100) / 100}</Extra> : '—'}</Td>
                                <Td align="right">
                                  {i.netValue > 0 ? <Short>{formatCurrency(i.netValue)}</Short> : <Extra>{formatCurrency(i.netValue)}</Extra>}
                                </Td>
                              </SubRow>
                            ))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <TotalRow>
                    <Td colSpan={4} style={{ textAlign: 'right' }}>Total</Td>
                    <Td align="right"><Short>{formatCurrency(totals.short)}</Short></Td>
                    <Td align="right"><Extra>{formatCurrency(totals.extra)}</Extra></Td>
                    <Td align="right">
                      {totals.net > 0 ? <Short>{formatCurrency(totals.net)}</Short> : <Extra>{formatCurrency(totals.net)}</Extra>}
                    </Td>
                  </TotalRow>
                </tfoot>
              </Table>
            )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

export default StockLossSummary;
